import { useRef } from 'react';
import { useApp } from '../AppContext';

const MAX_EDGE = 1600;   // px — plenty for a quote, small enough to email
const QUALITY = 0.8;

/** Downscale to a JPEG data URL so a dozen phone photos still fit in an email. */
function compress(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', QUALITY));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error(`${file.name} isn't a readable image`)); };
    img.src = url;
  });
}

/**
 * Photo list editor. `photos` is [{ url, ref? }] — `ref` marks one already
 * stored on the server; new ones carry only a data-URL `url`.
 */
export default function PhotoPicker({ photos, onChange, max }) {
  const fileRef = useRef();
  const { toast } = useApp();

  async function onPick(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const room = max - photos.length;
    if (files.length > room) toast(`Only ${room} more photo${room === 1 ? '' : 's'} fit on a proposal`);
    const added = [];
    for (const f of files.slice(0, Math.max(0, room))) {
      try { added.push({ url: await compress(f) }); } catch (err) { toast(err.message); }
    }
    if (added.length) onChange([...photos, ...added]);
  }

  return (
    <>
      {photos.length ? (
        <div className="photo-grid" style={{ marginTop: 'var(--s-3)' }}>
          {photos.map((p, i) => (
            <div className="photo-pending" key={p.ref || i}>
              <img className="photo-thumb" src={p.url} alt={`Proposal photo ${i + 1}`} />
              <button type="button" className="rm" aria-label={`Remove photo ${i + 1}`}
                onClick={() => onChange(photos.filter((_, j) => j !== i))}>×</button>
            </div>
          ))}
        </div>
      ) : null}
      <div className="row" style={{ gap: 'var(--s-2)', marginTop: 'var(--s-3)' }}>
        <button type="button" className="btn sm" onClick={() => fileRef.current?.click()} disabled={photos.length >= max}>
          📷 Add photos
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={onPick} />
        <span className="sb">{photos.length}/{max} photos</span>
      </div>
    </>
  );
}
